import type { IpAddressWhitelistTableRow } from '@/features/ip_address_whitelists/type';
import { useTranslation } from '@/i18n';
import {
  ButtonGroup,
  ButtonV2,
  Tooltip,
} from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { FC, memo } from 'react';
import styles from './IpAddressWhitelistRowActionsCell.module.scss';

const cx = classnames.bind(styles);

type Props = {
  row: IpAddressWhitelistTableRow;
  rowIndex: number;
  editingRowId: string | null;
  isInlineSaveDisabled: boolean;
  isEditingRestrict: boolean;
  isDeleteBlockedByRestriction: boolean;
  isAddModalOpen: boolean;
  onSaveInline: () => void;
  onCancelInline: () => void;
  onStartEdit: (row: IpAddressWhitelistTableRow) => void;
  onDeleteRow: (row: IpAddressWhitelistTableRow) => void | Promise<void>;
};

export const IpAddressWhitelistRowActionsCell: FC<Props> = memo(
  ({
    row,
    rowIndex,
    editingRowId,
    isInlineSaveDisabled,
    isEditingRestrict,
    isDeleteBlockedByRestriction,
    isAddModalOpen,
    onSaveInline,
    onCancelInline,
    onStartEdit,
    onDeleteRow,
  }) => {
    const { t } = useTranslation();

    if (row.id === editingRowId) {
      return (
        <ButtonGroup>
          <ButtonV2
            size='sm'
            onClick={onSaveInline}
            isDisabled={isInlineSaveDisabled}
          >
            {t('save')}
          </ButtonV2>
          <ButtonV2 size='sm' isSecondary onClick={onCancelInline}>
            {t('cancel')}
          </ButtonV2>
        </ButtonGroup>
      );
    }

    const isActionDisabled =
      isEditingRestrict ||
      isAddModalOpen ||
      (editingRowId !== null && editingRowId !== row.id);
    const isDeleteDisabled = isActionDisabled || isDeleteBlockedByRestriction;

    const deleteButton = (
      <ButtonV2
        isSecondary
        color='danger'
        isDisabled={isDeleteDisabled}
        onClick={() => {
          void onDeleteRow(row);
        }}
        testId={`ip_address_whitelist_table_delete-${rowIndex}`}
      >
        {t('delete')}
      </ButtonV2>
    );

    return (
      <ButtonGroup>
        <ButtonV2
          isSecondary
          isDisabled={isActionDisabled}
          onClick={() => onStartEdit(row)}
          testId={`ip_address_whitelist_table_edit-${rowIndex}`}
        >
          {t('edit')}
        </ButtonV2>
        {isDeleteBlockedByRestriction ? (
          <Tooltip
            title={t('ip_address_whitelist_table_delete_disabled_tooltip')}
            overlayClassName={cx('delete-disabled-tooltip')}
            placement='bottom'
          >
            {deleteButton}
          </Tooltip>
        ) : (
          deleteButton
        )}
      </ButtonGroup>
    );
  }
);
IpAddressWhitelistRowActionsCell.displayName =
  'IpAddressWhitelistRowActionsCell';
